import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatMs } from "@/utils/format";
import { packIntoTracks, type TraceLane, type TraceNode } from "@/utils/trace/model";
import { TraceBar, type TraceScale } from "./TraceBar";
import { SubAgentInlineSummary, laneNodeFor } from "./SubAgentSummary";

/**
 * Split a lane into the two things that share one row.
 *
 * `base` is the lane's own work — turns, model turns, spans. `overlay` is the
 * tool calls, drawn after so they sit on top of the turn that invoked them.
 * They take their own slice of the row rather than being nested inside it: the
 * turn only paints "waiting" where no tool of its own was running.
 */
function layersFor(lane: TraceLane): { base: TraceNode[]; overlay: TraceNode[] } {
  if (lane.kind === "main") {
    return {
      base: lane.nodes,
      overlay: lane.nodes.flatMap((turn) => turn.children),
    };
  }
  if (lane.kind === "sub_agent") {
    return {
      base: lane.nodes.filter((node) => node.kind === "sa_model_turn"),
      overlay: lane.nodes.filter((node) => node.kind === "sa_tool_call"),
    };
  }
  return { base: lane.nodes, overlay: [] };
}

/**
 * Longest first, so a short bar nested inside a long one ends up on top and
 * stays clickable instead of being swallowed by its neighbour.
 */
function longestFirst(nodes: TraceNode[]) {
  const span = (node: TraceNode) =>
    node.startMs === null || node.endMs === null ? 0 : node.endMs - node.startMs;
  return [...nodes].sort((a, b) => span(b) - span(a));
}

/**
 * The rows a lane draws, and what goes on each.
 *
 * One row is the normal answer. Splitting a lane was tried and reverted twice —
 * per turn, then per tool name — because splitting *unconditionally* fragments
 * one agent's work into a stack. The rule here is narrower: work that genuinely
 * ran at the same time cannot share a row, because on one row the bars are
 * painted over each other and only the widest survives. Nothing else splits, so
 * a serial trace is drawn exactly as it was.
 *
 * Within a row `longestFirst` turns the flat list into layers on its own: the
 * widest bar is painted first, so a sub-agent's execution envelope ends up
 * behind its model turns, which end up behind its tool calls.
 */
function rowsFor(lane: TraceLane): TraceNode[][] {
  const { base, overlay } = layersFor(lane);
  const baseTracks = packIntoTracks(base);
  const overlayTracks = packIntoTracks(overlay);

  // A single track of each is the shape every serial lane has, and there the
  // overlay belongs *on* the base — a tool bar taking its slice out of the turn
  // that invoked it. Only once either layer needs a second track does the
  // overlay move below, where it has room to spread.
  const rows =
    baseTracks.length <= 1 && overlayTracks.length <= 1
      ? [[...base, ...overlay]]
      : [...baseTracks, ...overlayTracks];

  // The envelope is what says how long the execution ran; a reconstructed lane
  // has nothing else that does, since its model turns were never recorded. Only
  // these two kinds get one — a live session's lane already *is* a single
  // envelope node, and drawing a second one over it would just double the bar.
  //
  // A nested lane is excluded for the same reason: it is built around the
  // durable job that ran it, so it already carries an envelope spanning more
  // than its own bounds. Synthesizing a second one would draw the same window
  // twice and lose the queue wait the job's bands mark.
  const envelope =
    (lane.kind === "sub_agent" || lane.kind === "tool_batch") && !lane.parentLaneId
      ? laneNodeFor(lane)
      : null;
  if (envelope) rows[0] = [envelope, ...rows[0]];

  const drawn = rows.filter((row) => row.length > 0);
  // An empty lane still draws its track, the same as before.
  return drawn.length > 0 ? drawn : [[]];
}

/** Indent per nesting level. One job sits under the delegation that submitted it. */
const DEPTH_INDENT_PX = 14;

export function TraceLaneRow({
  lane,
  scale,
  selectedNodeId,
  highlightedNodeIds,
  siblingLabel,
  depth = 0,
  onSelect,
}: {
  lane: TraceLane;
  scale: TraceScale;
  selectedNodeId: string | null;
  highlightedNodeIds: Set<string>;
  /** e.g. "batch 2/3" when this lane has siblings from the same dispatch. */
  siblingLabel?: string;
  /** How far inside another lane this one is drawn. */
  depth?: number;
  onSelect: (node: TraceNode) => void;
}) {
  const rows = rowsFor(lane);
  const laneDuration =
    lane.startMs !== null && lane.endMs !== null ? lane.endMs - lane.startMs : null;
  const laneNode = laneNodeFor(lane);

  const header = (
    <>
      {laneNode && (
        // Without this the clickable lane headers looked exactly like the
        // inert ones, so nobody found the sub-agent payload behind them.
        <ChevronRight className="size-3 shrink-0 text-muted-foreground" aria-hidden />
      )}
      <span className="font-medium">{lane.label}</span>
      {lane.sublabel && (
        <span className="font-mono text-muted-foreground">{lane.sublabel}</span>
      )}
      {siblingLabel && <span className="text-muted-foreground">{siblingLabel}</span>}
      {lane.status === "error" && (
        <span className="rounded-full border border-red-300 bg-red-50 px-1.5 font-bold text-red-700">
          {lane.rawStatus || "failed"}
        </span>
      )}
      {/* A delegated task that never came back is the thing a reviewer is
          looking for, and it has no bar to say so — it ends nowhere. */}
      {lane.status === "running" && (
        <span className="rounded-full border border-border bg-surface-subtle px-1.5 text-muted-foreground">
          {lane.rawStatus || "running"}
        </span>
      )}
      <span className="text-muted-foreground">
        {lane.status === "running" && lane.endMs === null
          ? "미완료"
          : formatMs(laneDuration)}
      </span>
    </>
  );

  // The indent goes on the header alone. The track has to keep the full width
  // it shares with the axis above — padding it would slide every bar off the
  // ticks by the indent, which is the same bug the label gutter once caused.
  const headerIndent = depth > 0 ? { paddingLeft: depth * DEPTH_INDENT_PX } : undefined;

  return (
    <div
      data-lane={lane.kind}
      data-depth={depth}
      // Only the outermost lanes carry a divider: a rule between a delegation
      // and the jobs inside it reads as a separation, which is the opposite of
      // what the indent is saying.
      className={cn(
        "grid gap-1 py-2",
        depth === 0 && "border-t border-border first:border-t-0",
      )}
    >
      {laneNode ? (
        <button
          type="button"
          onClick={() => onSelect(laneNode)}
          aria-pressed={lane.id === selectedNodeId}
          style={headerIndent}
          className={cn(
            "flex w-fit cursor-pointer items-center gap-1.5 rounded-sm px-1 py-0.5 text-left text-xs hover:bg-surface-subtle",
            lane.id === selectedNodeId && "bg-surface-subtle",
          )}
        >
          {header}
        </button>
      ) : (
        <div style={headerIndent} className="flex items-center gap-1.5 px-1 text-xs">
          {header}
        </div>
      )}
      <div className="grid gap-0.5">
        {rows.map((row, index) => (
          <div
            // Rows are a layout artefact with no identity of their own — which
            // node lands on which is decided by the packer, not by the data.
            key={index}
            data-row="lane"
            className="relative h-7 rounded-sm bg-surface-subtle"
          >
            {longestFirst(row).map((node) => (
              <TraceBar
                key={node.id}
                node={node}
                scale={scale}
                selected={node.id === selectedNodeId}
                highlighted={highlightedNodeIds.has(node.id)}
                onSelect={onSelect}
              />
            ))}
          </div>
        ))}
      </div>
      <SubAgentInlineSummary lane={lane} />
    </div>
  );
}
